import { CampaignDto, CampaignListItemDto } from '@shared/services/apiService/apiService';
import {
  CampaignListArg,
  pushCampaign,
  realtimeListsApi,
  useCampaignsPaginatedInfiniteQuery,
} from '@shared/services/apiService/realtimeListsApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/query';
import {
  acquireRealtimeSocket,
  type RealtimeSocketHandle,
} from '@shared/services/realtimeSocketManager';
import { useAppDispatch } from '@shared/store';
import type { RootState } from '@shared/store/store';

interface WsException {
  status: string;
  message: string;
}

type CampaignSocketRow = CampaignListItemDto & Partial<CampaignDto>;

interface UseWebSocketGetCampaignsOptions {
  limit?: number;
  sortBy?: 'title' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
  withTotal?: boolean;
  companyId?: string;
  search?: string;
}

export const useWebSocketGetCampaigns = (
  onError?: <T>(error: T) => void,
  options: UseWebSocketGetCampaignsOptions = {},
) => {
  const {
    limit = 20,
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    withTotal = false,
    companyId,
    search,
  } = options;
  const [realtime, setRealtime] = useState<RealtimeSocketHandle | null>(null);
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();

  const queryArg = useMemo<CampaignListArg>(
    () => ({
      limit,
      sortBy,
      sortOrder,
      ...(withTotal ? { withTotal } : {}),
      ...(search ? { search } : {}),
    }),
    [limit, search, sortBy, sortOrder, withTotal],
  );

  // Sorting or searching always reloads, as it did before the list lived in the cache
  const { data, hasNextPage, fetchNextPage, refetch, isFetching, isLoading, error } =
    useCampaignsPaginatedInfiniteQuery(queryArg, { refetchOnMountOrArgChange: true });

  // Pages can overlap when rows shift between requests; the first position and the latest row win
  const campaigns = useMemo(() => {
    const uniqueById = new Map<string, CampaignSocketRow>();
    data?.pages.forEach((page) => page.data.forEach((item) => uniqueById.set(item.id, item)));
    return Array.from(uniqueById.values());
  }, [data]);

  const refetchCampaignsList = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const loadMoreCampaigns = useCallback(async () => {
    if (!hasNextPage || isFetching) return;
    await fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching]);

  useEffect(() => {
    const handle = acquireRealtimeSocket(import.meta.env.VITE_API_URL, '/campaigns');
    setRealtime(handle);

    return () => {
      handle.release();
      setRealtime(null);
    };
  }, []);

  const subscriptionCompanyId = companyId || campaigns[0]?.companyId || '';

  useEffect(() => {
    if (!realtime || !subscriptionCompanyId) return;

    return realtime.joinRoom('subscribe_to_get_campaigns_list', {
      companyId: subscriptionCompanyId,
    });
  }, [realtime, subscriptionCompanyId]);

  useEffect(() => {
    if (!realtime) return;

    const onGetCampaignsList = (value: { result: CampaignDto }) => {
      dispatch(pushCampaign(queryArg, value.result));
    };

    const onException = (error: WsException) => {
      console.error(error);
      onError?.(error);
    };

    const unsubscribers = [
      realtime.on('get_campaigns_list', onGetCampaignsList),
      realtime.on('exception', onException),
      realtime.onMissedPushes(() =>
        dispatch(realtimeListsApi.util.invalidateTags(['CampaignList'])),
      ),
    ];

    return () => unsubscribers.forEach((off) => off());
  }, [realtime, dispatch, queryArg, onError]);

  // A campaign mutation invalidates CampaignList, and the reload starts as the mutation settles.
  // That reload is not one getRunningQueryThunk tracks, so this waits on the cache entry itself.
  const waitForRefresh = useCallback(
    () =>
      new Promise<void>((resolve) => {
        const selectEntry = realtimeListsApi.endpoints.campaignsPaginated.select(queryArg);
        const settle = () => {
          if (selectEntry(store.getState()).status === QueryStatus.pending) return;

          unsubscribe();
          resolve();
        };
        const unsubscribe = store.subscribe(settle);
        settle();
      }),
    [queryArg, store],
  );

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  return {
    isLoading,
    isFetching,
    data: campaigns,
    hasMore: !!hasNextPage,
    totalItems: data?.pages[0]?.meta.totalItems,
    loadMore: loadMoreCampaigns,
    refetch: refetchCampaignsList,
    waitForRefresh,
  };
};
